import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { openPositions } from '../data/careersData';
import { useLanguage } from '../contexts/LanguageContext';
import { setPageTitle } from '../utils/pageTitle';
import './CareersPage.css'; // Reusing some styles

const CareerDetailPage = () => {
  const { id } = useParams();
  const { t, localizedPath } = useLanguage();
  const position = openPositions.find(p => p.id === parseInt(id));

  useEffect(() => {
    if (position) {
      const title = t(`careers.jobs.${position.key}.title`);
      const desc = t(`careers.jobs.${position.key}.description`);
      setPageTitle(`${title} | Radix Technologies`, desc);
    } else {
      setPageTitle(t('careers.positionNotFound'), t('careers.positionNotFoundDesc'));
    }
  }, [position, t]);

  if (!position) {
    return (
      <div className="container section text-center">
        <h2>{t('careers.positionNotFound')}</h2>
        <p>{t('careers.positionNotFoundDesc')}</p>
        <Link to={localizedPath('/careers')} className="btn-primary" style={{ display: 'inline-block', marginTop: '2rem' }}>
          {t('careers.backToCareers')}
        </Link>
      </div>
    );
  }

  const jobTitle = t(`careers.jobs.${position.key}.title`);
  const jobDesc = t(`careers.jobs.${position.key}.description`);
  const jobReqs = t(`careers.jobs.${position.key}.requirements`, { returnObjects: true });
  const jobResps = t(`careers.jobs.${position.key}.responsibilities`, { returnObjects: true });
  const jobLocation = t(`careers.locations.${position.location}`);
  const jobType = t(`careers.types.${position.type}`);
  const jobDept = t(`careers.departments.${position.department}`);

  const mailtoLink = `mailto:careers@radixtechs.com?subject=${encodeURIComponent(`Application for ${jobTitle} - ${jobLocation}`)}&body=${encodeURIComponent(`Hi Radix Technologies Team,\n\nI would like to apply for the ${jobTitle} position (${jobType}) in ${jobLocation}.\n\n[Please attach your CV/Resume and provide any additional details here]\n\nBest regards,`)}`;

  return (
    <div className="careers-page">
      <section className="careers-hero section">
        <div className="container">
          <div className="careers-hero-content">
            <Link to={localizedPath('/careers')} className="back-link">
              ← {t('careers.backToCareers')}
            </Link>
            <h1 className="page-title">{jobTitle}</h1>
            <div className="position-meta detail-meta">
              <span className="position-department">{jobDept}</span>
              <span className="position-location">{jobLocation}</span>
              <span className="position-type">{jobType}</span>
              <span className="position-experience">{t('careers.experience')} {position.experience}</span>
              {position.status === 'hired' && (
                <span className="position-hired-badge position-hired-badge--large">{t('careers.positionFilledBadge')}</span>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <div className="career-detail-grid">
            <div className="career-detail-main">
              <div className="detail-section">
                <h2>{t('careers.aboutRole')}</h2>
                <p className="position-description">{jobDesc}</p>
              </div>

              {Array.isArray(jobReqs) && (
                <div className="detail-section">
                  <h2>{t('careers.requirements')}</h2>
                  <ul className="detail-list">
                    {jobReqs.map((req, index) => (
                      <li key={index}>{req}</li>
                    ))}
                  </ul>
                </div>
              )}

              {Array.isArray(jobResps) && (
                <div className="detail-section">
                  <h2>{t('careers.responsibilities')}</h2>
                  <ul className="detail-list">
                    {jobResps.map((resp, index) => (
                      <li key={index}>{resp}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <aside className="career-detail-sidebar">
              <div className="apply-card">
                {position.status === 'hired' ? (
                  <>
                    <h3>{t('careers.positionFilled')}</h3>
                    <div className="position-filled-notice">
                      <span className="position-filled-icon">✓</span>
                      <p>{t('careers.positionFilledMessage')}</p>
                    </div>
                    <span className="apply-btn apply-btn-disabled full-width">
                      {t('careers.positionFilled')}
                    </span>
                  </>
                ) : (
                  <>
                    <h3>{t('careers.interested')}</h3>
                    <p>{t('careers.applyPrompt')}</p>
                    <a href={mailtoLink} className="apply-btn full-width">
                      {t('careers.applyNow')}
                    </a>
                  </>
                )}
              </div>

              <div className="sidebar-info">
                <h4>{t('careers.shareJob')}</h4>
                <div className="share-links">
                  <button onClick={() => navigator.clipboard.writeText(window.location.href)} className="btn-primary btn-sm">
                    {t('careers.copyLink')}
                  </button>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CareerDetailPage;
